import React, { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";
import { useSelector } from "react-redux";
import ActivityCharts from "./ActivityCharts";
import UnauthorizedPage from "../UnauthorizedPage";

const apiUrl = process.env.REACT_APP_API_URL;

const Activity = ({ open, onClose }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [activityData, setActivityData] = useState([]);
  let accessToken = useSelector((state) => state.accessToken.value);
  const [error,setError] = useState(null);
  const getData = async () => {
    const query = `
      query MyQuery {
        getActivity {
          accounts
          action
          id
          timestamp
          username
        }
      }
    `;
    
    try {
      const response = await axios.post(
        apiUrl,
        { query },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      if (
        response.data.errors &&
        response.data.errors[0].errorType == "Unauthorized"
      ) {
        setError("Unauthorized");
      }
      if (response.data.data && response.data.data.getActivity) {
      const sortedData = response.data.data.getActivity.sort(
        (b, a) => new Date(a.timestamp) - new Date(b.timestamp)
      );
      setActivityData(sortedData);
    }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const totalPages = Math.ceil(activityData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = activityData.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    let pageNumbers = [];
    
    if (totalPages <= maxPagesToShow) {
      pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      pageNumbers.push(1);
      
      if (currentPage <= 3) {
        pageNumbers.push(2, 3, 4);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push('...');
        pageNumbers.push(totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pageNumbers.push('...');
        pageNumbers.push(currentPage - 1, currentPage, currentPage + 1);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  if (!open) return null;
  if(error == 'Unauthorized'){
    return (
      <UnauthorizedPage/>
    )
  }
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-[95%] max-w-7xl h-[95vh] flex flex-col">
        <div className="p-4 border-b flex justify-between items-center bg-[#003366]/5 shrink-0">
          <h2 className="text-2xl font-bold text-[#003366]">Activity Logs</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="border-b">
            <ActivityCharts activityData={activityData} />
          </div>

          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-[#003366]/5">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#003366] uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#003366] uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#003366] uppercase tracking-wider">
                      Accounts
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-[#003366] uppercase tracking-wider">
                      Time
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentData.map((activity) => (
                    <tr key={activity.id} className="hover:bg-[#003366]/5">
                      <td className="px-6 py-4 whitespace-nowrap text-[#003366] font-medium">
                        {activity.username}
                      </td>
                      <td className="px-6 py-4 text-gray-700">
                        {activity.action}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {activity.accounts}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {activity.timestamp}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 px-6 py-4 bg-white shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-700">
                Showing {startIndex + 1} to{" "}
                {Math.min(endIndex, activityData.length)} of {activityData.length}{" "}
                entries
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
              {getPageNumbers().map((page, index) => (
                page === '...' ? (
                  <span key={`ellipsis-${index}`} className="px-2 text-gray-600">...</span>
                ) : (
                  <button
                    key={`page-${page}`}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 rounded-lg ${
                      currentPage === page
                        ? "bg-[#003366] text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {page}
                  </button>
                )
              ))}
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages || totalPages === 0}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Activity;